import { useEffect, useMemo, useRef, useState } from "react";
import type {
  Attachment,
  CardView,
  Channel,
  ClusterContact,
  Message,
  NotifyPreview,
  NotifyTemplateKey,
  NotifyTemplateMeta,
} from "@breeze/shared";
import { CHANNELS } from "@breeze/shared";
import { useQuery } from "@tanstack/react-query";
import { useSendMessage } from "../hooks/useMessages.js";
import {
  fetchNotifyPreview,
  fetchNotifyTemplates,
  uploadAttachment,
} from "../api.js";
import { useCardCluster } from "../hooks/useCluster.js";

const CHANNEL_LABELS: Record<Channel, string> = {
  email: "Email",
  sms: "SMS",
  whatsapp: "WhatsApp",
  voip: "Call note",
};

// Collect deduped addresses usable on a given channel from the cluster's
// linked contacts. Emails for the email channel; mobile/home/work/main/other
// phones for SMS / WhatsApp. VoIP (call note) isn't a send — the composer
// logs a post-call note against the card, no recipient needed.
type RecipientOption = { value: string; label: string };
function collectRecipientOptions(
  channel: Channel,
  contacts: ClusterContact[]
): RecipientOption[] {
  if (channel === "voip") return [];
  const seen = new Set<string>();
  const out: RecipientOption[] = [];
  for (const c of contacts) {
    const who = c.fullName || `${c.firstName} ${c.lastName}`.trim() || `CID ${c.cid}`;
    if (channel === "email") {
      const v = c.email?.trim();
      if (v && !seen.has(v.toLowerCase())) {
        seen.add(v.toLowerCase());
        out.push({ value: v, label: `${v} · ${who}` });
      }
    } else {
      // SMS + WhatsApp share the same pool of phone numbers.
      for (const raw of [c.phoneMobile, c.phoneHome, c.phoneWork, c.phoneMain, c.phoneOther]) {
        const v = (raw ?? "").trim();
        if (!v) continue;
        const key = v.replace(/\D/g, "");
        if (!key || seen.has(key)) continue;
        seen.add(key);
        out.push({ value: v, label: `${v} · ${who}` });
      }
    }
  }
  return out;
}

// Pick the recipient the customer is most likely to reply on. For SMS/WA
// that's the phone number we most recently received an inbound message
// from; we match against the recipientOptions list (digits-only) so a
// stored TELEPHONE_MOBILE in "07…" form still resolves when the inbound
// arrived as "+44…". Falls back to the first option (mobile if present
// per collectRecipientOptions ordering) when there's no inbound history.
function pickPreferredRecipient(
  channel: Channel,
  options: RecipientOption[],
  messages: Message[]
): string {
  if (options.length === 0) return "";
  if (channel !== "sms" && channel !== "whatsapp") {
    return options[0]?.value ?? "";
  }
  const digitsOnly = (s: string): string => s.replace(/\D/g, "");
  const inboundMatch = messages.find(
    (m) =>
      m.direction === "in" &&
      (m.channel === "sms" || m.channel === "whatsapp") &&
      m.fromAddress &&
      m.fromAddress.trim().length > 0
  );
  if (inboundMatch?.fromAddress) {
    const wantedKey = digitsOnly(inboundMatch.fromAddress).slice(-10);
    if (wantedKey) {
      const hit = options.find(
        (o) => digitsOnly(o.value).slice(-10) === wantedKey
      );
      if (hit) return hit.value;
    }
  }
  return options[0]?.value ?? "";
}

function fmtSize(bytes: number | null): string {
  if (bytes === null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

type Props = {
  card: CardView;
  messages: Message[];
};

// --- Pending-action (AI draft) helpers ---------------------------------
// When a card has a pending AI draft we hydrate the composer with its
// contents instead of rendering a separate approval banner — the
// composer IS the editor; one scrollbar, one mental model.
async function acceptDraft(
  cardId: number,
  body: { subject?: string; bodyText: string; bodyHtml?: string }
): Promise<void> {
  const res = await fetch(`/api/cards/${cardId}/pending-action/accept`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`accept failed (${res.status})`);
}

async function dismissDraft(cardId: number): Promise<void> {
  const res = await fetch(`/api/cards/${cardId}/pending-action`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) throw new Error(`dismiss failed (${res.status})`);
}

export function Composer({ card, messages }: Props): JSX.Element {
  // Default channel = channel of the most recent message, else email.
  const defaultChannel: Channel = messages[0]?.channel ?? "email";

  const [channel, setChannel] = useState<Channel>(defaultChannel);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [to, setTo] = useState("");

  // Accept-mode: we've pre-filled the fields from a card.pendingAction and
  // the next Send routes through the accept endpoint. appliedDraftKey is
  // the pendingAction.createdAt we've already applied — lets us tell
  // "new draft arrived, hydrate again" from "user is editing the applied
  // draft, don't overwrite".
  const [appliedDraftKey, setAppliedDraftKey] = useState<string | null>(null);
  const [draftBusy, setDraftBusy] = useState<"idle" | "accepting" | "dismissing">("idle");
  const [draftError, setDraftError] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [uploading, setUploading] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  // Cluster drives the recipient dropdown — see collectRecipientOptions.
  const cluster = useCardCluster(card.cardId);
  const contacts = cluster.data?.cluster?.contacts ?? [];
  const visits = cluster.data?.cluster?.visits ?? [];

  const recipientOptions = useMemo(
    () => collectRecipientOptions(channel, contacts),
    [channel, contacts]
  );

  // ── Template quick-pick ────────────────────────────────────────────
  // Templates are rendered against the card's NEWEST visit (by VID desc) —
  // most cards have one visit and this matches the Notify modal behaviour.
  // If the card has no visit, the picker is disabled.
  const primaryVid = useMemo(() => {
    if (visits.length === 0) return null;
    return [...visits].sort((a, b) => b.vid - a.vid)[0]?.vid ?? null;
  }, [visits]);

  // Channel mapping: voip has no templates. Composer's email/sms/whatsapp
  // channel names match the NotifyTemplateMeta.channel values 1:1.
  const templatesQuery = useQuery<NotifyTemplateMeta[]>({
    queryKey: ["notify-templates"],
    queryFn: fetchNotifyTemplates,
    staleTime: 5 * 60 * 1000,
  });
  const allTemplates = templatesQuery.data ?? [];
  const channelTemplates = useMemo(
    () => allTemplates.filter((t) => t.channel === channel),
    [allTemplates, channel]
  );

  const [selectedKey, setSelectedKey] = useState<NotifyTemplateKey | "">("");
  const [preview, setPreview] = useState<NotifyPreview | null>(null);
  const [templateError, setTemplateError] = useState<string | null>(null);
  const [templateLoading, setTemplateLoading] = useState(false);

  // When channel changes, clear the loaded template — the picker's options
  // change so whatever was loaded no longer applies.
  useEffect(() => {
    setSelectedKey("");
    setPreview(null);
    setTemplateError(null);
  }, [channel]);

  // Fetch preview when a template is chosen. Fills subject + body + resets
  // any previous template state.
  useEffect(() => {
    if (!selectedKey || primaryVid === null) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    setTemplateLoading(true);
    setTemplateError(null);
    fetchNotifyPreview(primaryVid, selectedKey)
      .then((p) => {
        if (cancelled) return;
        setPreview(p);
        setBody(p.bodyText);
        if (p.subject !== null) setSubject(p.subject);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setTemplateError(err instanceof Error ? err.message : "preview failed");
        setPreview(null);
      })
      .finally(() => {
        if (!cancelled) setTemplateLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedKey, primaryVid]);

  // WA Content-template sends need the rendered body to match exactly. If
  // staff edited the body after loading, downgrade to freeform so we don't
  // send "template ABC" with completely different text.
  const useContentTemplate =
    preview !== null &&
    preview.contentSid !== null &&
    body === preview.bodyText;

  // When the channel changes OR the options list arrives, ensure `to` is
  // populated with a valid address. Reset if the current `to` isn't in the
  // options list (e.g. switching email → sms). For SMS/WA, prefer the
  // number the customer most recently messaged us on (Q8) so replies route
  // to the line they're actively using; falls back to the first option.
  useEffect(() => {
    if (channel === "voip") {
      setTo("");
      return;
    }
    const stillValid = recipientOptions.some((o) => o.value === to);
    if (!stillValid) {
      setTo(pickPreferredRecipient(channel, recipientOptions, messages));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel, recipientOptions.length]);

  const send = useSendMessage(card.cardId);

  // ── Pending-action (AI draft) hydration ────────────────────────────
  // When a new pending action arrives (server broadcast via SSE, or on
  // initial card load), pre-fill the composer with its channel /
  // recipient / subject / body. Identify "new" by pendingAction.createdAt
  // so once the user starts editing we don't keep stomping their input.
  const pa = card.pendingAction;
  const paKey = pa?.createdAt ?? null;
  useEffect(() => {
    if (!pa) {
      // Pending action cleared (accept succeeded, dismiss, or never had one).
      setAppliedDraftKey(null);
      setDraftError(null);
      return;
    }
    if (appliedDraftKey === paKey) return; // already hydrated this draft
    setChannel(pa.payload.channel);
    setSubject(pa.payload.subject ?? "");
    setBody(pa.payload.bodyText);
    setTo(pa.payload.to);
    setAppliedDraftKey(paKey);
    setDraftError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paKey, pa === null]);

  // In "accept mode" the next Send routes through /pending-action/accept
  // rather than a fresh /api/messages/send. Accept-mode ends when the
  // draft is accepted, dismissed, or the user manually switches channel
  // away from the draft's channel.
  const acceptMode =
    pa !== null && appliedDraftKey === paKey && channel === pa.payload.channel;

  const hasRecipient =
    channel === "voip" ? true : (to.trim().length > 0);
  const canSend =
    hasRecipient &&
    body.trim().length > 0 &&
    !send.isPending &&
    draftBusy === "idle" &&
    uploading === 0;

  const handleSubmit = (): void => {
    if (!canSend) return;

    // AI draft path — route through the approval endpoint so the server
    // clears pendingAction + stamps the outbound as agent='emma_draft'.
    if (acceptMode) {
      setDraftBusy("accepting");
      setDraftError(null);
      void acceptDraft(card.cardId, {
        ...(channel === "email" && subject.trim()
          ? { subject: subject.trim() }
          : {}),
        bodyText: body,
      })
        .then(() => {
          setBody("");
          setSubject("");
          setAttachments([]);
          setUploadError(null);
          setSelectedKey("");
          setPreview(null);
          // appliedDraftKey clears automatically when SSE nulls pa.
        })
        .catch((e: unknown) => {
          setDraftError(e instanceof Error ? e.message : String(e));
        })
        .finally(() => setDraftBusy("idle"));
      return;
    }

    const attachmentIds = attachments.map((a) => a.attachmentId);
    send.mutate(
      {
        cardId: card.cardId,
        channel,
        to: to.trim(),
        ...(channel === "email" && subject.trim()
          ? { subject: subject.trim() }
          : {}),
        bodyText: body,
        ...(attachmentIds.length > 0 ? { attachmentIds } : {}),
        ...(useContentTemplate && preview?.contentSid
          ? {
              contentSid: preview.contentSid,
              contentVariables: preview.contentVariables ?? {},
            }
          : {}),
      },
      {
        onSuccess: () => {
          setBody("");
          setSubject("");
          setAttachments([]);
          setUploadError(null);
          setSelectedKey("");
          setPreview(null);
        },
      }
    );
  };

  const handleDismissDraft = (): void => {
    if (!pa) return;
    setDraftBusy("dismissing");
    setDraftError(null);
    void dismissDraft(card.cardId)
      .then(() => {
        setBody("");
        setSubject("");
      })
      .catch((e: unknown) => {
        setDraftError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => setDraftBusy("idle"));
  };

  const handleFiles = async (files: FileList | null): Promise<void> => {
    if (!files || files.length === 0) return;
    setUploadError(null);
    const list = Array.from(files);
    setUploading((n) => n + list.length);
    try {
      const uploaded = await Promise.all(
        list.map((f) => uploadAttachment(f))
      );
      setAttachments((prev) => [...prev, ...uploaded]);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "upload failed");
    } finally {
      setUploading((n) => Math.max(0, n - list.length));
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeAttachment = (id: number): void => {
    setAttachments((prev) => prev.filter((a) => a.attachmentId !== id));
  };

  const allowAttachments = channel === "email";

  return (
    <div
      className={`border-t bg-ink-900/80 px-5 py-3 transition-colors ${
        acceptMode
          ? "border-slack-aubergineLight/60 bg-slack-aubergine/15"
          : "border-ink-700"
      }`}
    >
      {acceptMode && pa ? (
        <div className="mb-2 flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.14em] text-ink-50">
          <span className="sparkle-pulse">✨</span>
          <span>AI draft loaded · {pa.source}</span>
          <span className="ml-auto flex items-center gap-1.5 normal-case tracking-normal">
            {draftError ? (
              <span className="font-normal text-slack-pink">{draftError}</span>
            ) : null}
            <button
              onClick={handleDismissDraft}
              disabled={draftBusy !== "idle"}
              className="rounded-md border border-ink-500 px-2 py-0.5 text-[12px] font-normal text-ink-50 hover:border-slack-pink hover:text-slack-pink disabled:cursor-not-allowed disabled:opacity-50"
              title="Discard this AI draft (the card returns to a normal empty composer)"
            >
              {draftBusy === "dismissing" ? "…" : "Dismiss"}
            </button>
          </span>
        </div>
      ) : null}
      <div className="flex items-center gap-2">
        <div className="flex overflow-hidden rounded-md border border-ink-500 bg-ink-800">
          {CHANNELS.map((c) => (
            <button
              key={c}
              onClick={() => setChannel(c)}
              className={`px-2.5 py-1 text-[12px] uppercase tracking-wider transition-colors ${
                channel === c
                  ? "bg-slack-aubergine text-ink-50"
                  : "text-ink-50 hover:bg-ink-700"
              }`}
            >
              {CHANNEL_LABELS[c]}
            </button>
          ))}
        </div>
        {channel === "voip" ? null : recipientOptions.length === 0 ? (
          <div className="flex-1 rounded-md border border-slack-pink/50 bg-slack-pink/10 px-2.5 py-1.5 text-[12px] text-slack-pink">
            No {channel === "email" ? "email" : "phone"} on any contact. Add one via the Account tab →
          </div>
        ) : recipientOptions.length === 1 && recipientOptions[0] ? (
          <div
            className="flex-1 truncate rounded-md border border-ink-500 bg-ink-800 px-2.5 py-1.5 text-[13px] text-ink-50"
            title={recipientOptions[0].label}
          >
            {recipientOptions[0].label}
          </div>
        ) : (
          <select
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="flex-1 rounded-md border border-ink-500 bg-ink-900 px-2.5 py-1.5 text-[13px] text-ink-50 focus:border-slack-aubergineLight focus:outline-none"
          >
            {recipientOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        )}
      </div>
      {channel === "email" ? (
        <input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Subject"
          className="mt-2 w-full rounded-md border border-ink-500 bg-ink-900 px-2.5 py-1.5 text-[13px] text-ink-50 outline-none placeholder:text-ink-200 focus:border-slack-aubergineLight"
        />
      ) : null}
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder={`Write a ${CHANNEL_LABELS[channel].toLowerCase()}…`}
        rows={3}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === "Enter") handleSubmit();
        }}
        className="mt-2 w-full resize-none rounded-md border border-ink-500 bg-ink-900 px-2.5 py-2 text-[13px] leading-snug text-ink-50 outline-none placeholder:text-ink-200 focus:border-slack-aubergineLight"
      />
      {allowAttachments && (attachments.length > 0 || uploading > 0) ? (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {attachments.map((a) => (
            <span
              key={a.attachmentId}
              className="inline-flex items-center gap-1.5 rounded-md border border-ink-500 bg-ink-800 px-2 py-1 text-[12px] text-ink-50"
              title={`${a.filename} · ${fmtSize(a.sizeBytes)}`}
            >
              <span className="max-w-[12rem] truncate">{a.filename}</span>
              <span className="text-ink-200 tabular-nums">{fmtSize(a.sizeBytes)}</span>
              <button
                onClick={() => removeAttachment(a.attachmentId)}
                className="text-ink-200 hover:text-slack-pink"
                aria-label={`Remove ${a.filename}`}
              >
                ×
              </button>
            </span>
          ))}
          {uploading > 0 ? (
            <span className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-ink-500 bg-ink-800/60 px-2 py-1 text-[12px] text-ink-200">
              uploading… ({uploading})
            </span>
          ) : null}
        </div>
      ) : null}
      <div className="mt-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {allowAttachments ? (
            <>
              <input
                ref={fileRef}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => void handleFiles(e.target.files)}
              />
              <button
                onClick={() => fileRef.current?.click()}
                className="rounded-md border border-ink-500 px-2 py-1 text-[12px] text-ink-50 hover:border-ink-400"
                title="Attach files"
              >
                📎 Attach
              </button>
              {uploadError ? (
                <span className="text-[12px] text-slack-pink">{uploadError}</span>
              ) : null}
            </>
          ) : null}
          <span className="text-[12px] uppercase tracking-wider text-ink-200">
            {send.isError ? (
              <span className="text-slack-pink">send failed</span>
            ) : send.isPending || draftBusy === "accepting" ? (
              <span className="text-slack-yellow">
                {draftBusy === "accepting" ? "accepting…" : "queuing…"}
              </span>
            ) : (
              <>⌘↵ to send · {acceptMode ? "AI DRAFT" : CHANNEL_LABELS[channel]}</>
            )}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {channel === "voip" ? null : (
            <div className="flex items-center gap-1.5">
              <select
                value={selectedKey}
                onChange={(e) =>
                  setSelectedKey((e.target.value as NotifyTemplateKey) || "")
                }
                disabled={
                  primaryVid === null ||
                  templateLoading ||
                  channelTemplates.length === 0
                }
                className="rounded-md border border-ink-500 bg-ink-900 px-2 py-1 text-[12px] text-ink-50 focus:border-slack-aubergineLight focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                title={
                  primaryVid === null
                    ? "No visit linked to this card — templates need a visit for variable substitution"
                    : `Load a ${CHANNEL_LABELS[channel]} template`
                }
              >
                <option value="">
                  {templateLoading ? "Loading…" : "Use template…"}
                </option>
                {channelTemplates.map((t) => (
                  <option key={t.key} value={t.key}>
                    {t.label}
                  </option>
                ))}
              </select>
              {templateError ? (
                <span className="text-[11px] text-slack-pink">
                  ⚠ {templateError}
                </span>
              ) : preview && useContentTemplate ? (
                <span
                  className="text-[11px] text-slack-green"
                  title="Sending as an approved Twilio Content template (works outside the 24h session window)"
                >
                  template
                </span>
              ) : preview && preview.contentSid !== null ? (
                <span
                  className="text-[11px] text-slack-yellow"
                  title="You edited the template body — falling back to freeform. WhatsApp only delivers if you're inside the customer's 24h session window."
                >
                  edited · freeform
                </span>
              ) : null}
            </div>
          )}
          <button
            onClick={handleSubmit}
            disabled={!canSend}
            className="rounded-md border border-slack-aubergineLight bg-slack-aubergine px-3 py-1.5 text-[13px] font-medium text-ink-50 transition-colors hover:bg-slack-aubergineLight disabled:cursor-not-allowed disabled:border-ink-600 disabled:bg-ink-700 disabled:text-ink-300"
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
}
